import * as Accountant from 'app/calcs/Accountant'
import { Infobox } from 'app/components/Infobox';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { lifetimeBenefitsChartConfig, monthlyBenefitChartConfig } from 'app/containers/PipelineSection/SSA/chart'
import { IRetirementPerson, isValidPersonForSSA, peopleAreSameForSSA, Profile } from 'app/redux/Profile/model'
import { IWorkbookPayoutSchedule, SSAActions, WorkbookServiceResponse } from 'app/redux/SSA/actions'
import * as utils from 'app/utils'
import * as _ from 'lodash'
import * as moment from 'moment'
import 'moment-duration-format'
import * as React from 'react'
import { Button, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import * as ReactHighcharts from 'react-highcharts'
import { Redirect } from 'react-router';

type DataExtractor = (item: any) => number

interface ISSAMonthPayoutDetails {
  when: moment.Moment
  m: string,
  a: number
}

interface IFormattedMemberInfo {
  firstName: string,
  PIA: string,
  retirementAge: string,
  fullRetirementAge: string,
  lifeExpectancy,
}

interface IOptimizerInfo {
  p1RetirementAge: string,
  p2RetirementAge: string,
  benefitTypeSmallerPIA: string
}

interface ISSASummaryState {
  isInitialized: boolean,
  isCalculating: boolean,
  chartConfig: any,
  monthlyBenefitsChartConfig: any,
  profile: Profile,
  memberFormattedInfo: IFormattedMemberInfo[],
  earliestOptionTotal: string,
  latestOptionTotal: string,
  fullOptionTotal: string,
  selectedOptionTotal: string,
  optimalOptionTotal: string,
  optimizerInfo: IOptimizerInfo,
  //smallerPIAPerson: IFormattedMemberInfo,
  useOptimizedAges: boolean
}

const defaultMemberFormattedInfo = [
  {
    firstName: 'joe',
    PIA: '$1234',
    retirementAge: '66',
    fullRetirementAge: 'xx',
    lifeExpectancy: '90'
  },
  {
    firstName: 'betty',
    PIA: '$5678',
    retirementAge: '65',
    fullRetirementAge: 'xx',
    lifeExpectancy: '95'
  }
]
const defaultOptiomizerInfo = {
  p1RetirementAge: '66',
  p2RetirementAge: '66',
  benefitTypeSmallerPIA: 'own'
}

function toFormattedMemberInfo(m: IRetirementPerson): IFormattedMemberInfo {
  return {
    firstName: m.firstName,
    PIA: utils.toCurrency(m.PIA),
    lifeExpectancy: m.selectedAgeExpectancy + '',
    retirementAge: m.selectedRetirementAge + '',
    fullRetirementAge: '125'
  }
}

class SSAOptimizeContainer extends QuestionsGroupContainer<IQuestionsGroupContainerProps, ISSASummaryState> {
  state: ISSASummaryState = {
    isInitialized: false,
    isCalculating: false,
    chartConfig: undefined,
    monthlyBenefitsChartConfig: undefined,
    profile: undefined,
    memberFormattedInfo: defaultMemberFormattedInfo,
    earliestOptionTotal: '',
    fullOptionTotal: '',
    selectedOptionTotal: '',
    latestOptionTotal: '',
    optimalOptionTotal: '',
    optimizerInfo: defaultOptiomizerInfo,
    //smallerPIAPerson: toFormattedMemberInfo(Profile.createMember()),
    useOptimizedAges: false
  }

  lastMemberInfos: any[]
  whoaaNelly = _.debounce(this.updatePage.bind(this), 1000)

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info') || null
      const useOptimizedAges = profile.SSA.useOptimizedAges

      this.updatePage(profile)

      this.setState({ profile, useOptimizedAges })
    })
  }

  updatePage(profile: Profile) {
    if (this.state.isCalculating) { return } // if busy...
    const membersToConsider = profile.getPeopleForSSA()

    const memberInfos = membersToConsider.map(toFormattedMemberInfo)

    // we get a lot of state changes. Only is there is a change to the data
    // needed to perform SSA calculations, should we proceed and do them
    if (!_.isEqual(memberInfos, this.lastMemberInfos)) {
      this.lastMemberInfos = memberInfos
      this.setState({ isCalculating: true })

      Accountant.runSSAOptimizer(membersToConsider)
        .then((optimizerResults) => {
          const member = profile.getHeadOfHousehold()
          const ltb = optimizerResults.getLifetimeBenefits(member)
          const mb = optimizerResults.getMonthlyBenefits(member)
          const fras = optimizerResults.fullRetirementAges
          memberInfos[0].fullRetirementAge = utils.toNiceAge(fras[0])
          const optimizerInfo = optimizerResults.info
          let p2RetirementAge = 'n/a'
          if (membersToConsider.length > 1) {
            memberInfos[1].fullRetirementAge = utils.toNiceAge(fras[1])
            p2RetirementAge = utils.toNiceAge(optimizerInfo[1])
          }
          const newState = {
            chartConfig: this.generateChart(lifetimeBenefitsChartConfig, this.state.chartConfig, ltb),
            monthlyBenefitsChartConfig: this.generateMonthlyChart(optimizerResults.rawMonthlyPaymentSchedule),
            earliestOptionTotal: utils.toCurrency(_.last(ltb.earliest)[1], false),
            latestOptionTotal: utils.toCurrency(_.last(ltb.latest)[1], false),
            fullOptionTotal: utils.toCurrency(_.last(ltb.full)[1], false),
            selectedOptionTotal: utils.toCurrency(_.last(ltb.selected)[1], false),
            optimalOptionTotal: utils.toCurrency(_.last(ltb.optimal)[1], false),
            memberFormattedInfo: memberInfos,
            optimizerInfo: {
              p1RetirementAge: utils.toNiceAge(optimizerInfo[0], { showMonths: false }),
              p2RetirementAge: utils.toNiceAge(optimizerInfo[1], { showMonths: false }),
              benefitTypeSmallerPIA: optimizerInfo[6]
            },
            //: toFormattedMemberInfo(_(membersToConsider).orderBy((x) => x.PIA).first())
          }
          this.setState(newState)
        })
        .finally(() => {
          const isCalculating = false
          const isInitialized = true

          this.setState({ isCalculating, isInitialized })
        })
    }
  }

  onUseOptimalAges(x: any) {
    const profile = this.state.profile;
    const useOptimizedAges = !this.state.useOptimizedAges
    profile.SSA.useOptimizedAges = useOptimizedAges
    this.setState({ useOptimizedAges, profile })
    this.markSignificantProfileUpdate(profile)
  }

  updateMemberRetirementAge(deltaYears: number) {
    const profile = this.state.profile
    const member = profile.getHeadOfHousehold()
    const selectedRetirementAge = member.selectedRetirementAge + deltaYears
    if ((selectedRetirementAge >= 62) && (selectedRetirementAge <= 70)) {
      member.selectedRetirementAge = selectedRetirementAge
      this.setState({ profile })
      this.markSignificantProfileUpdate(profile)
    }
  }

  updateSpouseRetirementAge(deltaYears: number) {
    const profile = this.state.profile
    const spouse = profile.getSpouse()
    const selectedRetirementAge = spouse.selectedRetirementAge + deltaYears
    if ((selectedRetirementAge >= 62) && (selectedRetirementAge <= 70)) {
      spouse.selectedRetirementAge = selectedRetirementAge
      this.setState({ profile })
      this.markSignificantProfileUpdate(profile)
    }
  }

  // deprecated
  getPensionSentence() {
    if (null === this.state.profile) { return null }

    const member = this.state.profile.getHeadOfHousehold()
    const memberPensionWithdrawalAge = member.selectedRetirementAge ? moment.duration(member.selectedRetirementAge, 'year') : null
    const memberBeginPaymentsAgeText = utils.toNiceAge(memberPensionWithdrawalAge)

    const spouse = this.state.profile.getSpouse()
    const spousePensionWithdrawalAge = isValidPersonForSSA(spouse) ? moment.duration(spouse.selectedRetirementAge, 'year') : null
    //const spousePensionWithdrawalAge = (spouse.PIA > 0) && spouse.selectedRetirementAge ? moment.duration(spouse.selectedRetirementAge, 'year') : null
    const spouseBeginPaymentsAgeText = utils.toNiceAge(spousePensionWithdrawalAge)
    let pensionSentence = ''

    if (memberBeginPaymentsAgeText || spouseBeginPaymentsAgeText) {
      pensionSentence += 'Note: '

      if (memberBeginPaymentsAgeText) {
        pensionSentence += `${this.state.memberFormattedInfo[0].firstName}'s Pension benefits Withdrawal age is ${memberBeginPaymentsAgeText}. `
      }

      if (spouseBeginPaymentsAgeText && this.state.profile.spouseIsValid()) {
        pensionSentence += `${this.state.memberFormattedInfo[1].firstName}'s Pension benefits Withdrawal age is ${spouseBeginPaymentsAgeText}.`
      }
    }

    return pensionSentence
  }

  render() {
    if (null === this.state.profile) { return null }

    const member = this.state.profile.getHeadOfHousehold()
    if (0 === member.PIA) {
      // should not be allowed here, send them packing
      return <Redirect to="/socialsecurity/ssa_benefits"></Redirect>
    }

    const pensionSentence = undefined //this.getPensionSentence()
    const spouse = this.state.profile.getSpouse()
    const spouseIsValid = isValidPersonForSSA(spouse)
    const spouseHasPIA = spouseIsValid && (spouse.PIA > 0)
    // tslint:disable-next-line:no-console
    console.log('spouseIsValid? ' + spouseIsValid + ', spouseHasPIA: ' + spouseHasPIA)
    const spousePhrase = spouseIsValid ? ' and ' + spouse.firstName : ''

    return (
      <section>
        <header>
          <h1>Optimize Your Benefits</h1>
          <h5 className="lead">You{spousePhrase} can begin to draw Social Security benefits as early as age 62 and as late as age 70. Deciding when to begin to draw your benefits is very complicated. We've made the process much easier by providing you with a few analyses.</h5>

          {pensionSentence ?
            <h5>{pensionSentence}</h5> : null
          }
        </header>

        <section className="row" id="ssn-compareTable">
          <div className="col-md-6">
            <div className="ssn-compare">
              <div className="ssn-compare-head">
                <h3>Selected</h3>
              </div>
              {!this.state.isInitialized ?
                <div className="ssn-compare-content">
                  Loading&nbsp;<i className="fa fa-fw fa-spin fa-circle-o-notch" />
                </div>
                :
                <div className="ssn-compare-content animated fadeIn">
                  <p><small>Adjust withdrawal ages to see changes.
                  <Infobox>See charts <em>below</em> to see how amounts change over time.</Infobox>
                  </small></p>
                  <table className="table table-no-border">
                    <tbody>
                      <tr>
                        <td>
                          <b>{this.state.memberFormattedInfo[0].firstName} plans to draw benefits at &nbsp;<span className="text-success">{this.state.profile.family.members[0].selectedRetirementAge}</span></b>
                        </td>
                        <td>
                          <ButtonGroup bsSize="sm">
                            <Button style={{ marginRight: 0 }} onClick={() => this.updateMemberRetirementAge(-1)}>
                              <i className="fa fa-fw fa-chevron-down" />
                            </Button>
                            <Button bsStyle="primary" onClick={() => this.updateMemberRetirementAge(1)}>
                              <i className="fa fa-fw fa-chevron-up" />
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                      {spouseHasPIA && <tr>
                        <td>
                          <b>{this.state.memberFormattedInfo[1].firstName} plans to draw benefits at &nbsp;<span className="text-success">{this.state.profile.family.members[1].selectedRetirementAge}</span></b>
                        </td>
                        <td>
                          <ButtonGroup bsSize="sm">
                            <Button style={{ marginRight: 0 }} onClick={() => this.updateSpouseRetirementAge(-1)} >
                              <i className="fa fa-fw fa-chevron-down" />
                            </Button>
                            <Button bsStyle="primary" onClick={() => this.updateSpouseRetirementAge(1)}>
                              <i className="fa fa-fw fa-chevron-up" />
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>}
                    </tbody>
                  </table>
                </div>
              }
              <div className="ssn-compare-footer">
                <h4>
                  <sup>
                    <i className="fa"></i>
                  </sup>
                  {this.state.isCalculating ?
                    <i className="fa fa-fw fa-spin fa-circle-o-notch" />
                    :
                    <span>{this.state.selectedOptionTotal}</span>
                  }
                  <span className="ssn-compare-sm">Total Accumulated Lifetime Benefits </span>
                </h4>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="ssn-compare ssn-compare-active">
              <div className="ssn-compare-head grad-bg">
                <h3>Optimal</h3>
              </div>
              {!this.state.isInitialized ?
                <div className="ssn-compare-content">
                  Loading&nbsp;<i className="fa fa-fw fa-spin fa-circle-o-notch" />
                </div>
                :
                <div className="ssn-compare-content animated fadeIn">
                  <p><small>Based on the inputs given this option has the highest total.</small></p>
                  <table className="table table-no-border">
                    <tbody>
                      <tr>
                        <td>
                          <b>{this.state.memberFormattedInfo[0].firstName} draws benefits at &nbsp;{this.state.optimizerInfo.p1RetirementAge}</b>
                        </td>
                      </tr>
                      {spouseIsValid && <tr>
                        <td>
                          <b>{this.state.memberFormattedInfo[1].firstName} draws benefits at &nbsp;{this.state.optimizerInfo.p2RetirementAge}</b>
                        </td>
                      </tr>}
                      <tr>
                        <td colSpan={2}>
                          <label >
                            <input type="checkbox" onChange={(e) => this.onUseOptimalAges(e)}
                              checked={this.state.useOptimizedAges} />
                            &nbsp;<small>Use optimal ages for Retirement Summary</small  >
                          </label>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              }
              <div className="ssn-compare-footer">
                <h4>
                  <sup><i className="fa"></i></sup>
                  {this.state.isCalculating ?
                    <i className="fa fa-fw fa-spin fa-circle-o-notch" />
                    :
                    <span>{this.state.optimalOptionTotal}</span>
                  }
                  <span className="ssn-compare-sm">Total Accumulated Lifetime Benefits</span>
                </h4>
              </div>
            </div>
          </div>
        </section>
        <br />
        <section id="accumulatedLifetimeBenefitsChart">
          <h4>
            Accumulated Lifetime Benefits

            {this.state.isCalculating && this.state.isInitialized &&
              <span>
                &nbsp;<i className="fa fa-fw fa-spin fa-circle-o-notch" />
              </span>
            }
          </h4>
          <p>(<b>Tip:</b> You can hover over data points to get more info.)</p>
          {this.state.isInitialized ?
            <div className="well"><ReactHighcharts className="animated fadeIn"
              config={this.state.chartConfig} isPureConfig /></div>
            :
            <strong>
              Loading&nbsp;<i className="fa fa-fw fa-spin fa-circle-o-notch" />
            </strong>
          }
        </section>

        <section id="monthlyBenefits">
          <h4>
            Monthly Benefits

            {this.state.isCalculating && this.state.isInitialized &&
              <span>
                &nbsp;<i className="fa fa-fw fa-spin fa-circle-o-notch" />
              </span>
            }
          </h4>
          {this.state.isInitialized ?
            <div className="well">
              <ReactHighcharts className="animated fadeIn"
                config={this.state.monthlyBenefitsChartConfig} isPureConfig />
            </div>
            :
            <strong>
              Loading&nbsp;<i className="fa fa-fw fa-spin fa-circle-o-notch" />
            </strong>
          }
        </section>

        <footer className="margin-y">
          <a href="#/dashboard" className="backtodash-link">
            <i className="fa fa-fw fa-arrow-left" />
            &nbsp;Back to Dashboard
          </a>
        </footer>
      </section>
    )
  }

  private redrawNeeded(oldConfig: any, newConfig: any): boolean {
    if (!oldConfig) { return true }
    return !_.isEqual(oldConfig, newConfig)
  }

  private generateChart(referenceConfig: any, oldConfig: any, ltb: any): any {
    const newConfig = _.extend({}, referenceConfig, {
      xAxis: {
        max: _.last(ltb.earliest)[0]
      },
      series: [
        {
          name: 'Earliest Age',
          data: ltb.earliest,
          visible: false
        },
        {
          name: 'Latest Age',
          data: ltb.latest,
          visible: false
        },
        {
          name: 'Selected Retirement',
          data: ltb.selected,
        },
        {
          name: 'Full Retirement',
          data: ltb.full,
          visible: false
        },
        {
          name: 'Optimal',
          data: ltb.optimal,
          lineWidth: 3
        },
      ]
    })

    const rn = this.redrawNeeded(oldConfig, newConfig)

    return rn ? newConfig : oldConfig
  }

  private generateMonthlyChart(data: IWorkbookPayoutSchedule[]) {
    const extractSeries = (selectDataItem: DataExtractor) => {
      const r = _(data).groupBy((e) => e.when.year())
        .map((months) => {
          const activeMonths = _.filter(months, (x) => 0 !== selectDataItem(x)) as IWorkbookPayoutSchedule[]
          //return [ months[0].when.year(), _.meanBy(activeMonths, selectDataItem) || 0 ]
          return {
            x: months[0].when.year(),
            y: _.meanBy(activeMonths, selectDataItem) || 0,
            data: _(activeMonths)
              .map((z): ISSAMonthPayoutDetails => ({ when: z.when, m: z.when.format('MMM'), a: selectDataItem(z) }))
              .orderBy((z) => z.when.unix())
              .value()
          }
        })
        .filter((e) => 0 !== e.y)
        .value()
      return r
    }

    const optimalSeries = extractSeries((x: IWorkbookPayoutSchedule) => x.optimal)
    const newConfig = _.extend({}, monthlyBenefitChartConfig, {
      xAxis: {
        max: _.last(optimalSeries)[0]
      },
      series: [
        {
          name: 'Earliest Age',
          data: extractSeries((x: IWorkbookPayoutSchedule) => x.earliest),
          visible: false
        },
        {
          name: 'Latest Age',
          data: extractSeries((x: IWorkbookPayoutSchedule) => x.latest),
          visible: false
        },
        {
          name: 'Selected Retirement',
          data: extractSeries((x: IWorkbookPayoutSchedule) => x.selected),
        },
        {
          name: 'Full Retirement',
          data: extractSeries((x: IWorkbookPayoutSchedule) => x.full),
          visible: false
        },
        {
          name: 'Optimal',
          data: optimalSeries,
          lineWidth: 3
        },
      ]
    })

    const rn = this.redrawNeeded(this.state.monthlyBenefitsChartConfig, newConfig)

    return rn ? newConfig : this.state.monthlyBenefitsChartConfig
  }
}

const SSAOptimizeQuestionsGroup = () => (
  <SSAOptimizeContainer
    header="Optimize" icon="" />
)

export {
  ISSAMonthPayoutDetails,

  SSAOptimizeQuestionsGroup
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/SSA/Optimize/index.tsx